import { Box } from '@chakra-ui/react';
import { Checkbox, Text, Colors } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import Header from 'components/widgets/SearchFilterWizard/steps/components/Header';
import SectionWrapper from 'components/widgets/SearchFilterWizard/steps/components/SectionWrapper';
import { useWorkTypes } from 'state/dashboard';
import { WORK_TYPE_IDS } from 'utils/constants/workTypes';

const checkboxContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'padding_12',
  marginTop: 'padding_16',
  '@media (max-width: 1280px)': {
    gap: 'padding_8',
    marginTop: 'padding_12',
  },
  '@media (max-width: 1024px)': {
    gap: 'padding_8',
    marginTop: 'padding_8',
  },
};

const checkboxItemStyle = (isSelected: boolean) => ({
  padding: '8px',
  borderRadius: '4px',
  border: `1px solid ${isSelected ? Colors.ForestGreen900 : Colors.ArmyGreen200}`,
  backgroundColor: isSelected ? Colors.ArmyGreen100 : Colors.White,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  width: '100%',
  textAlign: 'left' as const,
  _hover: {
    borderColor: isSelected ? Colors.ForestGreen900 : Colors.ArmyGreen300,
    backgroundColor: isSelected ? Colors.ArmyGreen100 : Colors.ArmyGreen50,
  },
});

const WORK_TYPE_OPTIONS = [
  {
    value: WORK_TYPE_IDS.NEW_CONSTRUCTION,
    label: 'New Construction',
    mappedValues: [WORK_TYPE_IDS.NEW_CONSTRUCTION],
  },
  {
    value: 'interior-renovations-tenant-improvements',
    label: 'Interior Renovations / Tenant Improvements',
    // Maps to both "Tenant Improvement" and "Interior Renovation/Alteration"
    mappedValues: [WORK_TYPE_IDS.TENANT_IMPROVEMENT, WORK_TYPE_IDS.INTERIOR_RENOVATION],
  },
  {
    value: WORK_TYPE_IDS.EXTERIOR_RENOVATION,
    label: 'Exterior Renovation',
    mappedValues: [WORK_TYPE_IDS.EXTERIOR_RENOVATION],
  },
  {
    value: WORK_TYPE_IDS.DEMOLITION,
    label: 'Demolition',
    mappedValues: [WORK_TYPE_IDS.DEMOLITION],
  },
  {
    value: WORK_TYPE_IDS.TEMPORARY,
    label: 'Temporary',
    mappedValues: [WORK_TYPE_IDS.TEMPORARY],
  },
  {
    value: WORK_TYPE_IDS.OPEN_SPACE,
    label: 'Open Space',
    mappedValues: [WORK_TYPE_IDS.OPEN_SPACE],
  },
  {
    value: WORK_TYPE_IDS.SIGNS,
    label: 'Signs',
    mappedValues: [WORK_TYPE_IDS.SIGNS],
  },
];

type WorkTypeStepProps = {
  selectedWorkTypes: string[];
  onWorkTypesChange: (workTypes: string[]) => void;
};

const WorkTypeStep = ({ selectedWorkTypes, onWorkTypesChange }: WorkTypeStepProps) => {
  const [, setWorkTypes] = useWorkTypes();

  const handleWorkTypeToggle = (option: (typeof WORK_TYPE_OPTIONS)[0]) => {
    // If this option has mapped values (like Interior Renovations), toggle all mapped values
    const valuesToToggle: string[] = [...(option.mappedValues ?? [option.value])];

    const isSelected = valuesToToggle.some((val) => selectedWorkTypes.includes(val));

    let newWorkTypes: string[];
    if (isSelected) {
      // Remove all mapped values
      newWorkTypes = selectedWorkTypes.filter((val) => !valuesToToggle.includes(val));
    } else {
      // Add all mapped values
      newWorkTypes = Array.from(new Set([...selectedWorkTypes, ...valuesToToggle]));
    }

    onWorkTypesChange(newWorkTypes);
    setWorkTypes(newWorkTypes);
  };

  const isWorkTypeSelected = (option: (typeof WORK_TYPE_OPTIONS)[0]) => {
    const valuesToCheck: string[] = [...(option.mappedValues ?? [option.value])];

    return valuesToCheck.some((val) => selectedWorkTypes.includes(val));
  };

  return (
    <SectionWrapper>
      <Header
        title="New builds, renovations, or both?"
        description="Select your primary work types so we can tailor your matches."
      />
      <Box sx={checkboxContainerStyle}>
        {WORK_TYPE_OPTIONS.map((option) => {
          const isSelected = isWorkTypeSelected(option);

          return (
            <Box
              key={option.value}
              role="button"
              tabIndex={0}
              sx={checkboxItemStyle(isSelected)}
              onClick={() => handleWorkTypeToggle(option)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleWorkTypeToggle(option);
                }
              }}
              aria-pressed={isSelected}
            >
              <FlexRow
                sx={{ gap: 'padding_12', alignItems: 'flex-start', width: '100%' }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    paddingTop: '2px',
                  }}
                >
                  <Checkbox
                    isChecked={isSelected}
                    onToggle={() => handleWorkTypeToggle(option)}
                    sx={{ pointerEvents: 'none' }}
                  />
                </Box>
                <FlexColumn sx={{ flex: 1 }}>
                  <Text.Body sx={{ fontWeight: 600 }}>{option.label}</Text.Body>
                </FlexColumn>
              </FlexRow>
            </Box>
          );
        })}
      </Box>
    </SectionWrapper>
  );
};

export default WorkTypeStep;
